import { useAuth0 } from "@auth0/auth0-react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { adminPermissionKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import { Toast } from "../../../../components/Toast";
import { getPrettyDate, useLocalTime } from "../../../../utils/timestamp";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import ToEmailSection from "./ToEmailSection";

const PingManagerModal = ({ open, handleClose, userAdminRow }) => {
  const queryClient = useQueryClient();
  const colors = useColors();
  const { user } = useAuth0();
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const { sendEmailGRC } = useAPI();
  const showLocalTime = useLocalTime();

  const style = {
    bgcolor: colors.grey_50,
    p: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  };

  useEffect(() => {
    if (open) {
      setDisabledSubmit(false);
    }
  }, [open]);
  const [formData, setFormData] = useState({
    emails: [],
    to: "",
    manager: "",
    subject: "Action Required: User Permissions Approval",
    userName: "",
    lastSeen: "",
    accessPermissions: [],
    emailFrom: "",
    from: "",
    toolName: "",
  });
  const adminPermissionFormat = (adminPermission) => {
    const firstSeenByBreeze = adminPermission?.first_seen
      ? " - first seen as an admin on " +
        getPrettyDate(
          adminPermission?.first_seen?.timestamp_utc,
          showLocalTime
        ) +
        ", by Breeze"
      : "";
    return adminPermission.admin_type + firstSeenByBreeze;
  };

  useEffect(() => {
    if (userAdminRow) {
      const accessPermissions = userAdminRow?.admin_permissions?.map((admin) =>
        adminPermissionFormat(admin)
      );

      setFormData({
        emails: userAdminRow.hr_info?.manager_email
          ? [userAdminRow.hr_info?.manager_email]
          : [],
        to: "",
        manager: userAdminRow.hr_info?.manager_name || "",
        subject: "Action Required: User Permissions Approval",
        userName: userAdminRow?.name || "",
        lastSeen:
          getPrettyDate(
            userAdminRow?.last_seen_in_system?.timestamp_utc,
            showLocalTime
          ) || "",
        accessPermissions: accessPermissions.join("\n"),
        emailFrom: user.email || "",
        from: user.given_name,
        toolName: IntegrationsMapping[userAdminRow?.tool].displayName,
      });
    }
  }, [userAdminRow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = () => {
    setDisabledSubmit(true);
    if (formData.emails.length > 0) {
      sendEmailGRC(formData, userAdminRow.tool_email)
        .then(() => {
          Toast.success("Email sent successfully");
          queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
          handleClose();
        })
        .catch((error) => {
          setDisabledSubmit(false);
          if (error.response) {
            Toast.error(`An error occurred while trying to send the email.`);
          } else {
            Toast.error("An error occurred while processing the request.");
          }
        });
    } else {
      Toast.error("Please enter the recipient's email address.");
      setDisabledSubmit(false);
    }
  };
  const textFieldStyle = {
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
    "& .MuiInputBase-input": {
      color: colors.text_secondary,
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={style}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center" }}>
              Permissions Approval Request
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box
              sx={{
                backgroundColor: colors.primary_4percent,
                display: "flex",
                justifyContent: "center",
                height: 60,
                alignItems: "center",
                borderTop: "1px solid #e0e0e0",
                borderBottom: "1px solid #e0e0e0",
                padding: 2,
              }}
            >
              <Typography>
                {
                  "This email will notify the manager to review and approve the user’s access permissions "
                }
              </Typography>
            </Box>

            <ToEmailSection formData={formData} setFormData={setFormData} />
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 1,
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "baseline",
                backgroundColor: colors.common_white,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Subject:
              </Typography>
              <TextField
                variant="standard"
                fullWidth
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                sx={{ ...textFieldStyle }}
              />
            </Box>
            <Box
              sx={{
                border: "1px solid " + colors.divider,
                borderRadius: 2,
                padding: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                backgroundColor: colors.common_white,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    alignItems: "baseline",
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Hi
                  </Typography>

                  <TextField
                    variant="standard"
                    name="manager"
                    placeholder="Manager Name"
                    value={formData.manager}
                    onChange={handleChange}
                    margin="normal"
                    sx={{ ...textFieldStyle }}
                  />
                </Box>
                <Typography variant="body2" gutterBottom>
                  {"Please review and approve this user’s permissions for " +
                    IntegrationsMapping[userAdminRow?.tool].displayName}
                </Typography>
              </Box>
              <Divider />
              <TextField
                label="User Name"
                variant="standard"
                placeholder="User Name"
                fullWidth
                name="userName"
                value={formData.userName}
                onChange={handleChange}
                sx={{ ...textFieldStyle }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: colors.text_primary,
                    fontSize: "18px",
                    fontWeight: 500,
                  },
                }}
              />

              {formData?.lastSeen && (
                <DateField
                  name="lastSeen"
                  label="Last Seen in System"
                  value={dayjs(formData?.lastSeen, "DD/MM/YYYY")}
                  format="DD/MM/YYYY"
                  variant="standard"
                  sx={{ ...textFieldStyle }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      color: colors.text_primary,
                      fontSize: "18px",
                      fontWeight: 500,
                    },
                  }}
                />
              )}

              <TextField
                label="Access Permissions"
                variant="standard"
                fullWidth
                name="accessPermissions"
                value={formData.accessPermissions}
                placeholder="Access Permissions to Approved"
                onChange={handleChange}
                multiline
                rows={4} // Adjust the number of rows as needed
                sx={{ ...textFieldStyle }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: colors.text_primary,
                    fontSize: "18px",
                    fontWeight: 500,
                  },
                }}
              />
              <Divider />
              <TextField
                label="Please respond via email to"
                variant="standard"
                fullWidth
                name="emailFrom"
                placeholder="Email Address to Respond"
                value={formData.emailFrom}
                onChange={handleChange}
                sx={{ ...textFieldStyle }}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: colors.text_primary,
                    fontSize: "18px",
                    fontWeight: 500,
                  },
                }}
              />
              <Typography variant="body2">
                Thank you,
                <br />
                {formData.from}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={handleClose} color="primary">
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={disabledSubmit}
            >
              Send Email
            </PrimaryButton>
          </DialogActions>
        </Box>
      </LocalizationProvider>
    </Dialog>
  );
};

export default PingManagerModal;
